import { ItemListCarouselItem, ItemListItem } from "@/components/item-list/types";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { INoteObservable } from "@/store/note";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { SessionObservable } from "@/store/sessions/SessionObservable";
import { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { ReactNode } from "react";

export enum ItemListRowType {
  Skeleton = "Skeleton",
  Carousel = "Carousel",
  Item = "Item",
  SectionHeader = "SectionHeader",
  CollapsibleSectionHeader = "CollapsibleSectionHeader",
  Padding = "Padding",
  AsyncNote = "AsyncNote",
  AsyncCollection = "AsyncCollection",
  AsyncTemplate = "AsyncTemplate",
  AsyncSession = "AsyncSession",
  AsyncCalendarEvent = "AsyncCalendarEvent",
  Other = "Other",
}

interface IItemListRowData {
  type: ItemListRowType;
  key: string;
  payload: { [key: string]: unknown };
}

export interface ItemListAsyncNoteRowData extends IItemListRowData {
  type: ItemListRowType.AsyncNote;
  payload: {
    noteId: string;
    itemRow: ItemListItemRowData | ((note: INoteObservable) => ItemListItemRowData);
  };
}

export interface ItemListAsyncCollectionRowData extends IItemListRowData {
  type: ItemListRowType.AsyncCollection;
  payload: {
    collectionId: string;
    itemRow: ItemListItemRowData | ((collection: CollectionObservable) => ItemListItemRowData);
  };
}

export interface ItemListAsyncTemplateRowData extends IItemListRowData {
  type: ItemListRowType.AsyncTemplate;
  payload: {
    templateId: string;
    itemRow: ItemListItemRowData | ((template: TemplateObservable) => ItemListItemRowData);
  };
}

export interface ItemListAsyncSessionRowData extends IItemListRowData {
  type: ItemListRowType.AsyncSession;
  payload: {
    sessionId: string;
    itemRow: ItemListItemRowData | ((session: SessionObservable) => ItemListItemRowData);
  };
}

export interface ItemListAsyncCalendarEventRowData extends IItemListRowData {
  type: ItemListRowType.AsyncCalendarEvent;
  payload: {
    calendarEventId: string;
    itemRow:
      | ItemListItemRowData
      | ((calendarEvent: SpaceAccountCalendarEventObservable) => ItemListItemRowData);
  };
}

export interface ItemListSkeletonRowData extends IItemListRowData {
  type: ItemListRowType.Skeleton;
}

export interface ItemListCarouselRowData extends IItemListRowData {
  type: ItemListRowType.Carousel;
  payload: {
    items: ItemListCarouselItem[];
  };
}

export interface ItemListItemRowData extends IItemListRowData {
  type: ItemListRowType.Item;
  payload: ItemListItem;
  mustClickOnRowTitle?: boolean;
}

export interface ItemListSectionHeaderRowData extends IItemListRowData {
  type: ItemListRowType.SectionHeader;
  payload: {
    title: string;
  };
}

export interface ItemListCollapsibleSectionHeaderRowData extends IItemListRowData {
  type: ItemListRowType.CollapsibleSectionHeader;
  payload: {
    title: string;
    rightAddon?: ReactNode;
    isCollapsed: boolean;
    onCollapseChange: (collapsed: boolean) => void;
  };
}

export interface ItemListPaddingRowData extends IItemListRowData {
  type: ItemListRowType.Padding;
  payload: {
    height: number;
  };
}

export interface ItemListOtherRowData extends IItemListRowData {
  type: ItemListRowType.Other;
  payload: {
    children: ReactNode;
  };
}

export type ItemListRowData =
  | ItemListSkeletonRowData
  | ItemListCarouselRowData
  | ItemListItemRowData
  | ItemListSectionHeaderRowData
  | ItemListCollapsibleSectionHeaderRowData
  | ItemListPaddingRowData
  | ItemListAsyncNoteRowData
  | ItemListAsyncCollectionRowData
  | ItemListAsyncTemplateRowData
  | ItemListAsyncSessionRowData
  | ItemListAsyncCalendarEventRowData
  | ItemListOtherRowData;
